import DefaultFilterHandler from "@/components/DefaultFilterHandler";
import ExcelExportHandler from "@/components/excel-export";
import FilterModal from "@/components/filtering/Modal";
import { useCurrentPath } from "@/hooks";
import i18n from "@/i18n";
import { LocaleService, VersionService } from "@/services/global-services";
import { Language } from "@/services/global-services/models";
import { ContentService, ElementService } from "@/services/user-services";
import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import { Dictionary } from "@/utils/Dictionary";
import getUserLocale from "get-user-locale";
import { useEffect, useMemo, useRef, useState } from "react";
import { generatePath, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Footer from "./components/Footer";
import Header from "./components/Header";
import LoadingView from "./components/LoadingView";
import ScrollToTopButton from "./components/ScrollToTopButton";
import Wrapper from "./components/Wrapper";

const UserView = () => {
    const loading = useLocaleStore((state) => state.loading);
    const availableRegions = useLocaleStore(useShallow((state) => state.availableRegions));
    const availableLanguages = useLocaleStore(useShallow((state) => state.availableLanguages));
    const activeRegion = useLocaleStore(useShallow((state) => state.activeRegion));
    const activeLanguage = useLocaleStore(useShallow((state) => state.activeLanguage));
    const setLoading = useLocaleStore((state) => state.setLoading);
    const setAvailableRegions = useLocaleStore((state) => state.setAvailableRegions);
    const setAvailableLanguages = useLocaleStore((state) => state.setAvailableLanguages);
    const setActiveRegion = useLocaleStore((state) => state.setActiveRegion);
    const setActiveLanguage = useLocaleStore((state) => state.setActiveLanguage);

    const activeVersion = useFilterStore(useShallow((state) => state.activeVersion));
    const setActiveVersion = useFilterStore((state) => state.setActiveVersion);

    const versions = useDataStore((state) => state.versions);
    const setVersions = useDataStore((state) => state.setVersions);
    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const setPhases = useDataStore((state) => state.setPhases);
    const setSoftware = useDataStore((state) => state.setSoftware);
    const setUseCases = useDataStore((state) => state.setUseCases);
    const setPages = useDataStore((state) => state.setPages);
    const setElements = useDataStore((state) => state.setElements);
    const setFooterLinks = useDataStore((state) => state.setFooterLinks);

    const [canRender, setCanRender] = useState<boolean>(false);
    const getLocalisedContentCalled = useRef<string>("");

    const localeService = useMemo(
        () => new LocaleService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const contentService = useMemo(
        () => new ContentService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const elementService = useMemo(
        () => new ElementService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const currentPath = useCurrentPath();

    useEffect(() => {
        getLocales();
        getVersions();

        if (activeLanguage) i18n.changeLanguage(activeLanguage!);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!availableLanguages || Object.keys(availableLanguages).length === 0) return;

        if (!trySetActiveLocaleFromParams() && !trySetActiveFromStore() && !trySetActiveLocaleFromBrowser()) {
            const defaultRegion = availableRegions[0];
            const defaultLanguage = "en";

            setActiveRegion(defaultRegion.slug);
            setActiveLanguage(defaultLanguage);
        }

        setLoading(false);

        if (!params.locale) {
            navigate(`/${activeRegion}-${activeLanguage}${location.pathname.length > 1 ? location.pathname : ""}`, {
                replace: true,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableRegions, availableLanguages]);

    useEffect(() => {
        if (loading || versions.length === 0) return;

        getLocalisedContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, versions, activeVersion, activeRegion, activeLanguage]);

    useEffect(() => {
        if (loading) return;

        if (activeLanguage) i18n.changeLanguage(activeLanguage!);

        if (!params.locale) {
            navigate(`/${activeRegion}-${activeLanguage}${location.pathname.length > 1 ? location.pathname : ""}`, {
                replace: true,
            });
        } else if (params.locale !== `${activeRegion}-${activeLanguage}`) {
            const newPath = generatePath(currentPath, { ...params, locale: `${activeRegion}-${activeLanguage}` });
            navigate(newPath, { replace: false });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRegion, activeLanguage]);

    const getLocales = async () => {
        const availableLocales = await LocaleService.get();
        const activeLocales = availableLocales.filter((locale) => locale.active);
        const activeLanguages: Dictionary<Language[]> = {};
        activeLocales.forEach((locale) => {
            locale.languages.forEach((language) => {
                if (!activeLanguages[locale.regionCode]) activeLanguages[locale.regionCode] = [];
                if (language.active) activeLanguages[locale.regionCode].push(language);
            });
        });

        setAvailableRegions(activeLocales);
        setAvailableLanguages(activeLanguages);
    };

    const getVersions = async () => {
        const versions = await VersionService.get();
        const existingActiveVersionFromStore = versions.find((version) => version.id === activeVersion);
        const defaultVersion = versions.find((version) => version.isDefault)!;

        setVersions(versions);
        if (!existingActiveVersionFromStore && activeVersion !== "wip") setActiveVersion(defaultVersion.id);
    };

    const trySetActiveLocaleFromParams = (): boolean => {
        if (!params.locale) return false;

        const regionCode = params.locale.split("-")[0];
        const languageCode = params.locale.split("-")[1];
        const availableLanguagesForRegion = availableLanguages[regionCode];
        if (!availableLanguagesForRegion) return false;

        const isValidLocale = availableLanguagesForRegion.some((language) => language.slug === params.locale);
        if (!isValidLocale) return false;

        setActiveRegion(regionCode);
        setActiveLanguage(languageCode);

        return true;
    };

    const trySetActiveFromStore = (): boolean => {
        if (!activeRegion || !activeLanguage) return false;

        const availableLanguagesForRegion = availableLanguages[activeRegion];
        if (!availableLanguagesForRegion) return false;

        const isValidLocale = availableLanguagesForRegion.some(
            (language) => language.slug === `${activeRegion}-${activeLanguage}`
        );
        if (!isValidLocale) return false;

        setActiveRegion(activeRegion);
        setActiveLanguage(activeLanguage);

        return true;
    };

    const trySetActiveLocaleFromBrowser = (): boolean => {
        const browserLocale = getUserLocale({ useFallbackLocale: true, fallbackLocale: "be-en" }).toLowerCase();

        if (!browserLocale) return false;

        const hasCountryAndLanguage = browserLocale.split("-").length > 1;
        if (hasCountryAndLanguage) {
            const regionCode = browserLocale.split("-")[0];
            const languageCode = browserLocale.split("-")[1];
            const availableLanguagesForRegion = availableLanguages[regionCode];
            if (!availableLanguagesForRegion) return false;

            const isValidLocale = availableLanguagesForRegion.some(
                (language) => language.slug.toLowerCase() === browserLocale
            );
            if (!isValidLocale) return false;

            setActiveRegion(regionCode);
            setActiveLanguage(languageCode);
        } else {
            const languageCode = browserLocale;

            for (let i = 0; i < availableRegions.length; i++) {
                const regionCode = availableRegions[i];
                const availableLanguagesForRegion = availableLanguages[regionCode.slug];
                if (!availableLanguagesForRegion) continue;

                const isValidLocale = availableLanguagesForRegion.some(
                    (language) => language.slug.split("-")[1].toLowerCase() === languageCode.toLowerCase()
                );
                if (!isValidLocale) continue;

                setActiveRegion(regionCode.slug);
                setActiveLanguage(languageCode);

                return true;
            }
        }

        return false;
    };

    const getLocalisedContent = async () => {
        if (!activeVersion || getLocalisedContentCalled.current === `${activeRegion}-${activeLanguage}`) return;

        setCanRender(false);

        getLocalisedContentCalled.current = `${activeRegion}-${activeLanguage}`;

        let activeVersionName = versions.find((version) => version.id === activeVersion)?.name;
        if (!activeVersionName && activeVersion === "wip") {
            activeVersionName = "wip";
        }

        const pagesPromise = contentService.getPages().then(setPages);
        const footerLinksPromise = localeService.getFooterLinks().then(setFooterLinks);

        const activeVersionPromises = [];

        for (const version of versions) {
            const softwarePromise = localeService
                .getSoftware(version.name)
                .then((software) => setSoftware(version.id, software))
                .catch(() => setSoftware(version.id, []));

            const disciplinesPromise = localeService
                .getDisciplines(version.name)
                .then((disciplines) => setDisciplines(version.id, disciplines))
                .catch(() => setDisciplines(version.id, []));

            const phasesPromise = localeService
                .getPhases(version.name)
                .then((phases) => setPhases(version.id, phases))
                .catch(() => setPhases(version.id, []));

            const useCasesPromise = localeService
                .getUseCases(version.name)
                .then((useCases) => setUseCases(version.id, useCases))
                .catch(() => setUseCases(version.id, []));

            const elementsPromise = elementService
                .get(version.name)
                .then((elements) => setElements(version.id, elements))
                .catch(() => setElements(version.id, []));

            if (version.id === activeVersion) {
                activeVersionPromises.push(
                    elementsPromise,
                    softwarePromise,
                    disciplinesPromise,
                    phasesPromise,
                    useCasesPromise
                );
            }
        }

        await Promise.all([pagesPromise, footerLinksPromise, ...activeVersionPromises]);

        setCanRender(true);
    };

    if (!canRender) {
        return <LoadingView />;
    }

    return (
        <Wrapper>
            <Header />
            <Outlet />
            <Footer />
            <FilterModal />
            <DefaultFilterHandler />
            <ExcelExportHandler />
            <ScrollToTopButton />
        </Wrapper>
    );
};

export default UserView;
